import { createContext, useContext, useState, useCallback, FC, ReactNode } from 'react'
import { Id, toast } from 'react-toastify'
import { callValidationErrorToast } from 'src/hooks/useToast'

export interface ValidationError {
  ErrorCode: string
  ErrorMessage: string
  PropertyName: string
  AttemptedValue: string
  CustomState: any
  Severity: number
  FormattedMessagePlaceholderValues: {
    PropertyName?: string
    PropertyValue?: string
    CollectionIndex?: string
  };
}

interface ErrorContextData {
  errors: ValidationError[];
  toastId: Id;
  hasError(errorCode: string): boolean;
  getErrorMessage(errorCode: string): string;
  setValidationErrors(errors: ValidationError[]): ValidationError[];
  triggerToast(errors: ValidationError[]): void;
  clearToast(): void;
}

const ErrorContext = createContext<ErrorContextData>({} as ErrorContextData)

export const ErrorProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [errors, setErrors] = useState([] as ValidationError[])
  const [toastId, setToastId] = useState('' as Id)

  const hasError = (errorCode: string): boolean => {
    return errors?.filter((el: ValidationError) => el.ErrorCode === errorCode).length > 0;
  }

  const getErrorMessage = (errorCode: string): string => {
    return errors?.filter((el: ValidationError) => el.ErrorCode === errorCode)[0]?.ErrorMessage
  }

  const setValidationErrors = (errors: ValidationError[]): ValidationError[] => {
    const errorsWithIndicies = errors.map(e => {

      if (typeof e.FormattedMessagePlaceholderValues?.CollectionIndex != "number") {
        return e
      } else if (e.PropertyName.match(/\d+/g)!.length == 2) {
        return {
          ...e,
          ErrorCode: `${e.ErrorCode}:${e.PropertyName.match(/\d+/g)![0]}:${e.PropertyName.match(/\d+/g)![1]}`
        }
      } else if (e.PropertyName.match(/\d+/g)!.length == 1) {
        return {
          ...e,
          ErrorCode: `${e.ErrorCode}:${e.PropertyName.match(/\d+/g)![0]}`
        }
      } else {
        return e
      }
    })
    setErrors(errorsWithIndicies)
    return errorsWithIndicies
  }

  const triggerToast = (errors: ValidationError[]) => {
    setToastId(callValidationErrorToast(setValidationErrors(errors), { type: 'error' }));
  }

  const clearToast = () => {
    toast.dismiss(toastId)
    setToastId('')
    setValidationErrors([])
  }

  return (
    <ErrorContext.Provider
      value={{
        errors,
        toastId,
        hasError,
        getErrorMessage,
        setValidationErrors,
        triggerToast,
        clearToast,
      }}
    >
      {children}
    </ErrorContext.Provider>
  )
}

export function useErrors() {
  const context = useContext(ErrorContext)

  if (!context) throw new Error('useErrors must be used within an ErrorProvider!')

  return context
}